import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ChatIcon from '@mui/icons-material/Chat';

/**
 * Component to display clarification history for an invoice
 */
const ClarificationHistory = ({ clarifications = [] }) => {
  if (!clarifications || clarifications.length === 0) {
    return null;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'responded':
        return 'info';
      case 'pending':
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved':
        return 'הושלם - מאושר';
      case 'responded':
        return 'הושלם - נענה';
      case 'pending':
      default:
        return 'ממתין לתגובה';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'responded':
        return <CheckCircleIcon fontSize="small" />;
      case 'pending':
      default:
        return <HourglassEmptyIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Accordion defaultExpanded={clarifications.some(c => c.status === 'pending')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon color="info" />
            <Typography variant="subtitle1">
              היסטוריית בירורים ({clarifications.length})
            </Typography>
            {clarifications.some(c => c.status === 'pending') && (
              <Chip 
                label="בירור פתוח" 
                color="warning" 
                size="small" 
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {clarifications.map((clarification, index) => (
            <Box key={clarification._id} sx={{ mb: index < clarifications.length - 1 ? 2 : 0 }}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: clarification.status === 'pending' ? 'warning.main' : 'grey.200'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.primary">
                    נשלח ל: {clarification.assignedTo?.name || '-'}
                  </Typography>
                  <Chip 
                    icon={getStatusIcon(clarification.status)}
                    label={getStatusLabel(clarification.status)} 
                    color={getStatusColor(clarification.status)}
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary">
                  נשלח על ידי: {clarification.requestedBy?.name || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  תאריך שליחה: {formatDate(clarification.createdAt)}
                </Typography>

                {clarification.response?.text && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      תגובה:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      bgcolor: 'background.paper', 
                      p: 1.5, 
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {clarification.response.text}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      נענה ב: {formatDate(clarification.response.respondedAt)}
                    </Typography>
                  </>
                )}
              </Box>
              
              {index < clarifications.length - 1 && (
                <Divider sx={{ my: 1 }} />
              )}
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ClarificationHistory;
